/**
 * Admin users API layer (STORY-008-007 UI consuming STORY-008-011's
 * `GET /admin/users`). The endpoint is gated server-side on `users.read` and
 * returns ONLY PII-safe fields (id, display_name, email, status, roles,
 * created_at — never password_hash / phone / OAuth ids).
 *
 * Unlike the seller/product admin lists, this endpoint DOES support a free-text
 * `q` (case-insensitive match on email OR display_name) — so search is
 * server-side here.
 */
import { apiClient } from "@/lib/api-client";

/** One user row (mirrors `AdminUserDto` — PII-safe projection). */
export interface AdminUser {
  id: string;
  display_name: string;
  email: string;
  status: string;
  roles: string[];
  created_at: string;
}

/** Page envelope (mirrors `AdminUserListResponse`). */
export interface AdminUserListResponse {
  items: AdminUser[];
  next_cursor: string | null;
}

export const userKeys = {
  list: (filters: Record<string, unknown>) =>
    ["admin", "users", filters] as const,
  roles: () => ["admin", "rbac", "roles"] as const,
  permissions: () => ["admin", "rbac", "permissions"] as const,
} as const;

/** One role (mirrors `RoleOut`). */
export interface AdminRole {
  slug: string;
  name: string;
  is_system: boolean;
  permissions: string[];
}
/** One permission (mirrors `PermissionOut`). */
export interface PermissionItem {
  slug: string;
  description: string;
}
/** A named permission group (mirrors `PermissionGroupOut`). */
export interface PermissionGroup {
  group: string;
  permissions: PermissionItem[];
}
/** Catalogue (mirrors `PermissionCatalogueResponse`). */
export interface PermissionCatalogue {
  groups: PermissionGroup[];
}
/** Role-assign result (mirrors `RoleAssignResponse`). */
export interface RoleAssignResponse {
  user_id: string;
  roles: string[];
}

export function listRoles(): Promise<{ items: AdminRole[] }> {
  return apiClient.get<{ items: AdminRole[] }>("/admin/roles");
}
export function listPermissions(): Promise<PermissionCatalogue> {
  return apiClient.get<PermissionCatalogue>("/admin/permissions");
}
export function assignUserRoles(
  userId: string,
  roleSlugs: string[],
): Promise<RoleAssignResponse> {
  // PUT auto-attaches JWT + Idempotency-Key (REQ-ADD-§6.3-A) via apiFetch.
  return apiClient.put<RoleAssignResponse, { role_slugs: string[] }>(
    `/admin/users/${userId}/roles`,
    { role_slugs: roleSlugs },
  );
}

export interface ListUsersParams {
  q?: string | null;
  cursor?: string | null;
  limit?: number;
}

export function listUsers(
  params: ListUsersParams,
): Promise<AdminUserListResponse> {
  const query = new URLSearchParams();
  if (params.q) query.set("q", params.q);
  if (params.cursor) query.set("cursor", params.cursor);
  if (params.limit) query.set("limit", String(params.limit));
  const qs = query.toString();
  return apiClient.get<AdminUserListResponse>(
    `/admin/users${qs ? `?${qs}` : ""}`,
  );
}
